/** ***********************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 ************************************************************************* */

import React, { Component } from 'react';
import { observer } from 'mobx-react';
import { getHashParam } from '../../../../utils/urlUtils';
import { ANALYTICS, APPS_COMPONENT_ID } from '../../../../utils/enums';
import { CatalogAnalytics, analyticsSharedData } from '../../../../analytics';
import AppSection from '../../../../components/appSection';

const COMPONENT_ID = 'AppTiles';

const setSendAnalyticBoolean = (routingProps) => {
    // this checks to skip multiple render of apps/all/desktop
    // need to remove once the issue is fixed. - Sunil
    if (routingProps && routingProps.location && routingProps.location.pathname) {
        CatalogAnalytics.sendAnalyticsBool = window
            .appContext ? true // Always send analytics for CCD
            : (CatalogAnalytics.routingPath !== routingProps.location.pathname);
        if (CatalogAnalytics.sendAnalyticsBool) {
            CatalogAnalytics.routingPath = routingProps.location.pathname;
        }
        // TODO: only single event being sent after wam connection
        if (analyticsSharedData.wamConnected && analyticsSharedData.shouldSendWamAppsRenderEvent) {
            CatalogAnalytics.sendAnalyticsBool = true;
        }
    }
};

class AppTiles extends Component {
    appTilesRef = null;

    componentDidMount() {
        this.scrollToAppCard();

        const {
            appConfig, appsSkeletonModel,
        } = this.props;
        if (analyticsSharedData.wamConnected && analyticsSharedData.shouldSendWamAppsRenderEvent) {
            analyticsSharedData.appsRenderAnalyticsArray = CatalogAnalytics.appsAnalyticsArray;
            CatalogAnalytics.appsAnalyticsArray = [];
        }
        if (CatalogAnalytics.appsAnalyticsArray.length) {
            CatalogAnalytics.sendAnalytics(appConfig, appsSkeletonModel,
                ANALYTICS.EVENTS.APPS_ARRAY,
                { appsAnalyticsArray: CatalogAnalytics.appsAnalyticsArray });
        }
    }

    componentDidUpdate() {
        this.scrollToAppCard();
    }

    scrollToAppCard = () => {
        try {
            // Should return if invoked when component is not mounted yet
            if (!this.appTilesRef) {
                return;
            }

            const { history, ...routingProps } = this.props;

            // Extract context (i.e., sapCode_codexVersion) from routing location's hash values
            let context = getHashParam('context', routingProps.location);
            if (!context) {
                return;
            }

            // Escape `.` character which may be prsent in codexVersion,
            // else querySelector would fail
            context = context.replace(/\./g, '\\.');

            // Expecting that there is only one card with the provided context
            // (i.e., sapcode and version)
            let appTileEl = this.appTilesRef.querySelector(`.appTile_container.${context}`);

            // TODO(maniredd): Need to make a generic className for all layouts and make sure css
            // of existing layouts don't break.
            if (!appTileEl) {
                appTileEl = this.appTilesRef.querySelector(`.listView_appTile_container.${context}`);
            }

            if (appTileEl) {
                const appTileElRect = appTileEl.getBoundingClientRect();
                const isAppTileElInViewport = (
                    appTileElRect.top >= 0
                    && appTileElRect.left >= 0
                    && appTileElRect.bottom <= (
                        window.innerHeight || document.documentElement.clientHeight
                    )
                    && appTileElRect.right <= (
                        window.innerWidth || document.documentElement.clientWidth
                    )
                );

                // If the App card is already visibile then do not scroll
                if (!isAppTileElInViewport) {
                    appTileEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
            }

            // Replace the location to be without hash
            // so that re-scroll does not happen due to any reason
            // like if user scrolls out of the App card view
            history.replace(routingProps.location.pathname);
        } catch (e) {
            console.warn(`${APPS_COMPONENT_ID}: ${COMPONENT_ID}: routeToAppCard: Exception while routing to app tile: message: ${e.message}`);
        }
    }

    render() {
        const {
            appConfig, appsSkeletonModel, allAppsModel, appSection,
            subSection = {}, layouts, ...routingProps
        } = this.props;
        let { sectionHeaderList } = appSection;
        const { sectionHeaderList: subSectionHeaderList } = subSection;
        if (subSectionHeaderList) {
            sectionHeaderList = subSectionHeaderList;
        }
        setSendAnalyticBoolean(routingProps);
        CatalogAnalytics.appsAnalyticsArray = [];
        if (!sectionHeaderList || !sectionHeaderList.length) {
            return (
                <div className="app-tiles-container" ref={(el) => { this.appTilesRef = el; }}>
                    <AppSection
                        appConfig={appConfig}
                        appsSkeletonModel={appsSkeletonModel}
                        allAppsModel={allAppsModel}
                        routingProps={routingProps}
                        appSection={appSection}
                        subSection={subSection}
                        layouts={layouts}
                    />
                </div>
            );
        }
        return (
            <div className="app-tiles-container" ref={(el) => { this.appTilesRef = el; }}>
                {sectionHeaderList.map((sectionHeader, index) => (
                    <AppSection
                        key={`appSection-${index + 1}`}
                        appConfig={appConfig}
                        appsSkeletonModel={appsSkeletonModel}
                        allAppsModel={allAppsModel}
                        routingProps={routingProps}
                        appSection={appSection}
                        subSection={subSection}
                        sectionHeader={sectionHeader}
                        layouts={layouts}
                    />
                ))}

            </div>
        );
    }
}

export default observer(AppTiles);
